/**
 * Name a setup before it is saved. Drawn inside the lab rather than through
 * the app's modal portal, because a teacher saves from a bench that may be
 * full screen on a projector — and a portal outside the full-screen element
 * is not shown at all.
 */
import { useEffect, useRef, useState } from 'react';
import { labSetups } from '@padcanvas/shared';
import './labSetups.css';

export interface SaveSetupDialogProps {
    heading: string;
    initialName: string;
    initialNotes?: string;
    confirmLabel?: string;
    busy?: boolean;
    error?: string | null;
    /** The chapters the lab belongs to. With more than one, the teacher picks
     *  which this setup is listed under on the Chapters tab; with one, it is
     *  that one, and nothing is asked. */
    chapters?: readonly ChapterOption[];
    /** Ticked to start with. Absent means every one of `chapters`. */
    initialChapterIds?: readonly string[];
    /** `chapterIds` is undefined when the lab named no chapters. */
    onSave: (name: string, notes: string, chapterIds?: string[]) => void;
    onCancel: () => void;
}

export interface ChapterOption { id: string; label: string }

export function SaveSetupDialog({
    heading, initialName, initialNotes = '', confirmLabel = 'Save', busy = false, error, chapters = [], initialChapterIds, onSave, onCancel,
}: SaveSetupDialogProps): React.JSX.Element {
    const [name, setName] = useState(initialName);
    const [notes, setNotes] = useState(initialNotes);
    // Only ids the lab offers: a setup's own list may name a chapter it no longer has.
    const [ticked, setTicked] = useState<string[]>(() => chapters.map(c => c.id)
        .filter(id => !initialChapterIds || initialChapterIds.includes(id)));
    const toggle = (id: string): void => setTicked(t => (t.includes(id) ? t.filter(x => x !== id) : [...t, id]));
    const nameRef = useRef<HTMLInputElement>(null);
    useEffect(() => {
        nameRef.current?.focus();
        nameRef.current?.select();
    }, []);

    const submit = (e: React.FormEvent): void => {
        e.preventDefault();
        if (!name.trim() || busy) return;
        // One chapter is not a choice: the setup is under it.
        const chapterIds = chapters.length > 1 ? chapters.map(c => c.id).filter(id => ticked.includes(id))
            : chapters.length ? [chapters[0].id] : undefined;
        onSave(name.trim(), notes.trim(), chapterIds);
    };

    return (
        <div className="labsetup-overlay" onMouseDown={e => { if (e.target === e.currentTarget && !busy) onCancel(); }}>
            <form className="labsetup-dialog" role="dialog" aria-modal="true" aria-label={heading} onSubmit={submit}
                onKeyDown={e => { if (e.key === 'Escape' && !busy) { e.stopPropagation(); onCancel(); } }}>
                <h3>{heading}</h3>
                <label>
                    <span>Name</span>
                    <input ref={nameRef} value={name} maxLength={labSetups.LAB_SETUP_NAME_MAX} onChange={e => setName(e.target.value)}
                        placeholder="Convex lens — object at F, for 10B" />
                </label>
                <label>
                    <span>Notes <em>(optional)</em></span>
                    <textarea value={notes} maxLength={labSetups.LAB_SETUP_NOTES_MAX} rows={3} onChange={e => setNotes(e.target.value)}
                        placeholder="What it shows, or what to ask the class" />
                </label>
                {chapters.length > 1 && (
                    <fieldset className="labsetup-chapters">
                        <legend>Show under <em>(on the Chapters tab)</em></legend>
                        {chapters.map(c => (
                            <label key={c.id}>
                                <input type="checkbox" checked={ticked.includes(c.id)} onChange={() => toggle(c.id)} />
                                <span>{c.label}</span>
                            </label>
                        ))}
                        {ticked.length === 0 && <p className="labsetup-hint">Under no chapter — it will be in My setups only.</p>}
                    </fieldset>
                )}
                {error && <p className="labsetup-error" role="alert">{error}</p>}
                <div className="labsetup-actions">
                    <button type="button" onClick={onCancel} disabled={busy}>Cancel</button>
                    <button type="submit" className="labsetup-primary" disabled={busy || !name.trim()}>
                        {busy ? 'Saving…' : confirmLabel}
                    </button>
                </div>
            </form>
        </div>
    );
}
